import { splitSentences, tokenize } from './text';

/**
 * 长难句识别（纯规则，先判"是不是"，再交给 AI 讲"为什么"）。
 *
 * 分工很重要：
 * - **识别**必须便宜、确定、可离线：一篇文章里哪些句子值得分析，用规则算就够了，
 *   而且导入时就要把 `is_long` 落库（不能等 AI），否则列表页没法筛。
 * - **讲解**（主干、从句类型、翻译、仿写）才是 AI 的活，见 apps/api 的 sentence.service。
 *
 * 判定口径按产品规格：**长度 > 25 词**，或**从句数 ≥ 2**，或**含复杂修饰**
 * （非谓语结构 / 介词链 / 插入语 / 倒装）。三条只要中一条就算长难句 ——
 * 宁可多标几句：多分析一句的代价是几厘钱，漏掉一句的代价是用户看不到该看的句子。
 *
 * 所有阈值都是可读的经验值，`score` 用于排序（先看最难的），`reasons` 用于解释。
 */

export type ClauseType = 'relative' | 'adverbial' | 'nominal' | 'non-finite' | 'coordinate';

export const CLAUSE_TYPE_LABELS: Record<ClauseType, string> = {
  relative: '定语从句',
  adverbial: '状语从句',
  nominal: '名词性从句',
  'non-finite': '非谓语结构',
  coordinate: '并列结构',
};

/** 从句引导词 → 从句类型。 */
const CLAUSE_MARKERS: ReadonlyMap<string, ClauseType> = new Map<string, ClauseType>([
  // 定语从句
  ['which', 'relative'], ['who', 'relative'], ['whom', 'relative'], ['whose', 'relative'],
  ['where', 'relative'], ['when', 'relative'], ['why', 'relative'],
  // 状语从句
  ['because', 'adverbial'], ['although', 'adverbial'], ['though', 'adverbial'], ['since', 'adverbial'],
  ['unless', 'adverbial'], ['until', 'adverbial'], ['till', 'adverbial'], ['while', 'adverbial'],
  ['whereas', 'adverbial'], ['whenever', 'adverbial'], ['wherever', 'adverbial'], ['if', 'adverbial'],
  ['lest', 'adverbial'], ['provided', 'adverbial'], ['assuming', 'adverbial'], ['supposing', 'adverbial'],
  // 名词性从句
  ['whether', 'nominal'], ['what', 'nominal'], ['whatever', 'nominal'], ['whoever', 'nominal'],
  ['whichever', 'nominal'], ['how', 'nominal'],
  // 并列
  ['and', 'coordinate'], ['but', 'coordinate'], ['or', 'coordinate'], ['nor', 'coordinate'],
  ['yet', 'coordinate'],
]);

/** 多词引导词（先匹配长的，避免 `so that` 被当成 `so`）。 */
const PHRASAL_MARKERS: readonly { pattern: string[]; type: ClauseType }[] = [
  { pattern: ['so', 'that'], type: 'adverbial' },
  { pattern: ['in', 'order', 'that'], type: 'adverbial' },
  { pattern: ['even', 'though'], type: 'adverbial' },
  { pattern: ['even', 'if'], type: 'adverbial' },
  { pattern: ['as', 'if'], type: 'adverbial' },
  { pattern: ['as', 'though'], type: 'adverbial' },
  { pattern: ['as', 'long', 'as'], type: 'adverbial' },
  { pattern: ['as', 'soon', 'as'], type: 'adverbial' },
  { pattern: ['the', 'moment'], type: 'adverbial' },
  { pattern: ['no', 'matter'], type: 'adverbial' },
  { pattern: ['such', 'that'], type: 'adverbial' },
];

/** 介词。连续出现会形成"介词链"（the effect of the lack of …），是长难句的典型特征。 */
const PREPOSITIONS = new Set([
  'of', 'in', 'on', 'at', 'by', 'for', 'with', 'without', 'from', 'to', 'into', 'onto',
  'about', 'above', 'across', 'after', 'against', 'along', 'among', 'around', 'as', 'before',
  'behind', 'below', 'beneath', 'beside', 'between', 'beyond', 'despite', 'during', 'except',
  'inside', 'like', 'near', 'off', 'out', 'outside', 'over', 'past', 'regarding', 'since',
  'through', 'throughout', 'toward', 'towards', 'under', 'until', 'upon', 'within',
]);

/**
 * `that` 的双重身份。
 *
 * 它是最常见的从句引导词（名词性从句与定语从句都用它），但同时也是指示代词
 * （`that book`）。没有词性标注的情况下，用一个很窄的启发式区分：
 * **只有当 `that` 后面紧跟代词 / 限定词 / 助动词 / `there` 时才算从句引导词**。
 *
 *   the fact that he left        → 后接代词 → 算从句 ✓
 *   I think that the plan works  → 后接限定词 → 算从句 ✓
 *   that is why                  → 后接助动词 → 算从句（略高估，可接受）
 *   that book is good            → 后接名词 → 不算 ✓
 *
 * 漏掉 `that` 的代价比略高估大得多：它会让大量真实的定语/名词性从句不被计数，
 * 长难句识别直接失效。
 */
const THAT_CONTINUATIONS = new Set([
  'i', 'you', 'he', 'she', 'it', 'we', 'they', 'there',
  'the', 'a', 'an', 'this', 'these', 'those', 'his', 'her', 'its', 'their', 'our', 'your', 'my',
  'is', 'are', 'was', 'were', 'be', 'been', 'am',
  'has', 'have', 'had', 'do', 'does', 'did',
  'will', 'would', 'can', 'could', 'should', 'may', 'might', 'must', 'shall',
]);

/**
 * 看起来像非谓语、其实是名词的 -ing 词。
 *
 * 非谓语结构（现在分词/动名词短语）是复杂修饰的重要信号，但"以 -ing 结尾"这个判据
 * 会把 `morning`、`building`、`nothing` 一并算进来。这些词在真实文本里出现频率很高，
 * 不排除掉的话，几乎所有含 `something` 的句子都会被动语态/非谓语阈值推成长难句。
 */
const ING_NOUNS = new Set([
  'morning', 'evening', 'building', 'meeting', 'feeling', 'meaning', 'nothing', 'something',
  'anything', 'everything', 'during', 'spring', 'string', 'thing', 'king', 'ring', 'wing',
  'clothing', 'shopping', 'housing', 'sightseeing', 'outstanding', 'willing', 'interesting',
  'exciting', 'surprising',
]);

export interface LongSentenceFeatures {
  tokenCount: number;
  /** 从句/引导结构的总数（不含并列） */
  clauseCount: number;
  /** 出现过的从句类型（去重） */
  clauseTypes: ClauseType[];
  /** 介词个数 */
  prepositionCount: number;
  /** 非谓语结构个数（to do / doing 短语 / done 短语） */
  nonFiniteCount: number;
  /** 插入语个数（成对逗号、破折号或括号） */
  insertionCount: number;
}

export type LongSentenceReason =
  | 'length'
  | 'clauses'
  | 'non-finite'
  | 'preposition-chain'
  | 'insertion'
  | 'nesting';

export const REASON_LABELS: Record<LongSentenceReason, string> = {
  length: '句子过长',
  clauses: '从句嵌套',
  'non-finite': '非谓语结构',
  'preposition-chain': '介词链',
  insertion: '插入修饰',
  nesting: '多层修饰',
};

export interface LongSentenceVerdict {
  isLong: boolean;
  /** 0–100 的"难"度，用于排序与展示 */
  score: number;
  reasons: LongSentenceReason[];
  features: LongSentenceFeatures;
  /** 逐条中文说明，可直接上屏（例如「长度 34 词（阈值 25）」） */
  notes: string[];
}

/** 阈值。改动这里就能整体收紧/放宽长难句的判定。 */
export const LONG_SENTENCE_THRESHOLDS = {
  /** 产品规格：> 25 词 */
  tokenCount: 25,
  /** 产品规格：从句数 ≥ 2 */
  clauseCount: 2,
  /** 从句数 ≥ 2 时的最小长度，避免把 "If so, do it." 之类短句算成长难句 */
  clauseMinTokens: 14,
  /** 非谓语结构 ≥ 2 处视为复杂修饰 */
  nonFiniteCount: 2,
  /** 介词 ≥ 5 个视为介词链 */
  prepositionCount: 5,
  /** 插入语 ≥ 1 处 */
  insertionCount: 1,
} as const;

function countInsertions(sentence: string): number {
  let count = 0;
  // 成对括号 / 破折号
  count += (sentence.match(/\([^()]{1,80}\)/g) ?? []).length;
  count += Math.floor((sentence.match(/\s-\s/g) ?? []).length / 2);
  // 句中成对逗号：逗号 + 至少 3 个词 + 逗号（粗略，但足以识别同位语/插入语）
  const commaPairs = sentence.match(/,\s+(?:[A-Za-z'-]+\s+){2,8}[A-Za-z'-]+,/g);
  if (commaPairs) count += commaPairs.length;
  return count;
}

/** 抽取特征。纯统计，可对单句调用，也可对整句列表调用。 */
export function extractLongSentenceFeatures(text: string): LongSentenceFeatures {
  const tokens = tokenize(text);
  const lemmas = tokens.map((token) => token.lemma);

  const clauseTypes = new Set<ClauseType>();
  let clauseCount = 0;

  // 先用多词引导词"吃掉"位置，避免重复计数
  const consumed = new Array<boolean>(lemmas.length).fill(false);
  for (const marker of PHRASAL_MARKERS) {
    for (let i = 0; i + marker.pattern.length <= lemmas.length; i += 1) {
      let hit = true;
      for (let j = 0; j < marker.pattern.length; j += 1) {
        if (consumed[i + j] || lemmas[i + j] !== marker.pattern[j]) {
          hit = false;
          break;
        }
      }
      if (hit) {
        for (let j = 0; j < marker.pattern.length; j += 1) consumed[i + j] = true;
        clauseCount += 1;
        clauseTypes.add(marker.type);
      }
    }
  }

  let prepositionCount = 0;
  let nonFiniteCount = 0;

  for (let i = 0; i < lemmas.length; i += 1) {
    const lemma = lemmas[i]!;
    const surface = tokens[i]!.surface;

    if (!consumed[i]) {
      const type = CLAUSE_MARKERS.get(lemma);
      if (type !== undefined && type !== 'coordinate') {
        // 并列连词不计入"从句"，但会记进 clauseTypes 供分析展示
        clauseCount += 1;
        clauseTypes.add(type);
      } else if (type === 'coordinate') {
        clauseTypes.add('coordinate');
      } else if (lemma === 'that') {
        // 见 THAT_CONTINUATIONS 的说明：用一个窄启发式区分从句引导词与指示代词
        const next = lemmas[i + 1];
        if (next !== undefined && THAT_CONTINUATIONS.has(next)) {
          clauseCount += 1;
          clauseTypes.add('nominal');
        }
      }
    }

    if (PREPOSITIONS.has(lemma)) prepositionCount += 1;

    // 非谓语：to + 动词原形
    if (lemma === 'to' && i + 1 < lemmas.length && /^[a-z]+$/.test(lemmas[i + 1] ?? '')) {
      nonFiniteCount += 1;
    }
    // 现在分词/动名词短语（排除 -ing 名词，见 ING_NOUNS）
    // 注意用 `normalized`（文中形式）而不是 `lemma`：词形还原会把 learning → learn、
    // morning → morn，用 lemma 去比对 -ing 名词表永远不会命中。
    if (surface.length > 4 && /ing$/.test(surface) && !ING_NOUNS.has(tokens[i]!.normalized)) {
      nonFiniteCount += 1;
    }
    // 过去分词短语（V-ed 作后置定语）
    if (surface.length > 4 && /ed$/.test(surface) && i > 0) nonFiniteCount += 1;
  }

  return {
    tokenCount: tokens.length,
    clauseCount,
    clauseTypes: [...clauseTypes],
    prepositionCount,
    nonFiniteCount,
    insertionCount: countInsertions(text),
  };
}

/** 打分 + 判定。`score` 与 `isLong` 相互独立：短但极复杂的句子也会被标出来。 */
export function detectLongSentence(text: string): LongSentenceVerdict {
  const features = extractLongSentenceFeatures(text);
  const { tokenCount, clauseCount, nonFiniteCount, prepositionCount, insertionCount } = features;
  const reasons: LongSentenceReason[] = [];
  const notes: string[] = [];

  if (tokenCount > LONG_SENTENCE_THRESHOLDS.tokenCount) {
    reasons.push('length');
    notes.push(`长度 ${tokenCount} 词（阈值 ${LONG_SENTENCE_THRESHOLDS.tokenCount}）`);
  }
  if (
    clauseCount >= LONG_SENTENCE_THRESHOLDS.clauseCount &&
    tokenCount >= LONG_SENTENCE_THRESHOLDS.clauseMinTokens
  ) {
    reasons.push('clauses');
    notes.push(`含 ${clauseCount} 处从句引导结构`);
  }
  if (nonFiniteCount >= LONG_SENTENCE_THRESHOLDS.nonFiniteCount) {
    reasons.push('non-finite');
    notes.push(`含 ${nonFiniteCount} 处非谓语结构`);
  }
  if (prepositionCount >= LONG_SENTENCE_THRESHOLDS.prepositionCount) {
    reasons.push('preposition-chain');
    notes.push(`含 ${prepositionCount} 个介词修饰`);
  }
  if (insertionCount >= LONG_SENTENCE_THRESHOLDS.insertionCount) {
    reasons.push('insertion');
    notes.push(`含 ${insertionCount} 处插入修饰`);
  }
  if (clauseCount >= 3) {
    reasons.push('nesting');
    notes.push('出现三层以上修饰嵌套');
  }

  // 难度分：长度与从句主导，修饰成分补充。封顶 100。
  const score = Math.min(
    100,
    Math.round(
      Math.max(0, tokenCount - 8) * 2.2 +
        clauseCount * 11 +
        nonFiniteCount * 3 +
        Math.max(0, prepositionCount - 2) * 2.5 +
        insertionCount * 6,
    ),
  );

  const isLong =
    reasons.includes('length') ||
    reasons.includes('clauses') ||
    reasons.includes('non-finite') ||
    reasons.includes('preposition-chain') ||
    reasons.includes('insertion') ||
    reasons.includes('nesting');

  if (!isLong) notes.push('句子结构较简单，未达长难句阈值');

  return { isLong, score, reasons, features, notes };
}

/** 便捷版：从整篇文本里挑出长难句（返回句子文本与判定结果）。 */
export function findLongSentences(text: string): { text: string; verdict: LongSentenceVerdict }[] {
  return splitSentences(text)
    .map((sentence) => ({ text: sentence, verdict: detectLongSentence(sentence) }))
    .filter((item) => item.verdict.isLong);
}
